import {
  AnalyticsFiltersSnapshot,
  SerializedAnalyticsFilters,
  toSerializedAnalyticsFilters,
} from 'app/modules/analytics/utils/analyticsFiltersSerialization';

/**
 * Param names match the legacy insights module so links minted there keep
 * meaning the same thing here.
 */
export const ANALYTICS_FILTERS_QUERY_KEYS = {
  startDate: 'startDate',
  endDate: 'endDate',
  datePreset: 'preselect',
  comparisonStartDate: 'comparedStartDate',
  comparisonEndDate: 'comparedEndDate',
  comparisonDatePreset: 'comparePreset',
} as const;

const OWNED_KEYS = Object.values(ANALYTICS_FILTERS_QUERY_KEYS);

export function hasAnalyticsFiltersQueryParams(search: string): boolean {
  const params = new URLSearchParams(search);
  return OWNED_KEYS.some((key) => params.has(key));
}

/**
 * Reads the filter params into the same shape local storage holds, so both
 * sources resolve through `resolveAnalyticsFilters` and agree on the rules —
 * notably that a preset outranks the literal dates beside it.
 */
export function readAnalyticsFiltersFromQuery(
  search: string,
): Partial<SerializedAnalyticsFilters> | null {
  if (!hasAnalyticsFiltersQueryParams(search)) {
    return null;
  }

  const params = new URLSearchParams(search);
  const startDate = params.get(ANALYTICS_FILTERS_QUERY_KEYS.startDate);
  const endDate = params.get(ANALYTICS_FILTERS_QUERY_KEYS.endDate);
  const datePreset = params.get(ANALYTICS_FILTERS_QUERY_KEYS.datePreset);
  const comparisonStartDate = params.get(
    ANALYTICS_FILTERS_QUERY_KEYS.comparisonStartDate,
  );
  const comparisonEndDate = params.get(
    ANALYTICS_FILTERS_QUERY_KEYS.comparisonEndDate,
  );
  const comparisonDatePreset = params.get(
    ANALYTICS_FILTERS_QUERY_KEYS.comparisonDatePreset,
  );

  return {
    ...(startDate !== null && { startDate }),
    ...(endDate !== null && { endDate }),
    datePreset,
    // A compare window in the link is the switch: there is no separate flag to
    // contradict.
    isComparisonEnabled: Boolean(comparisonStartDate && comparisonEndDate),
    ...(comparisonStartDate !== null && { comparisonStartDate }),
    ...(comparisonEndDate !== null && { comparisonEndDate }),
    comparisonDatePreset,
  };
}

/**
 * Writes the dates and nothing else. The presets stay out of the link — they
 * are still read, so a link that names one keeps working, but a link this app
 * mints says only which days it is showing.
 */
export function applyAnalyticsFiltersToQuery(
  params: URLSearchParams,
  snapshot: AnalyticsFiltersSnapshot,
): void {
  const persisted = toSerializedAnalyticsFilters(snapshot);

  params.set(ANALYTICS_FILTERS_QUERY_KEYS.startDate, persisted.startDate);
  params.set(ANALYTICS_FILTERS_QUERY_KEYS.endDate, persisted.endDate);
  // A preset left behind by an incoming link would outrank the dates just
  // written, so it goes.
  params.delete(ANALYTICS_FILTERS_QUERY_KEYS.datePreset);
  params.delete(ANALYTICS_FILTERS_QUERY_KEYS.comparisonDatePreset);

  if (!snapshot.isComparisonEnabled) {
    params.delete(ANALYTICS_FILTERS_QUERY_KEYS.comparisonStartDate);
    params.delete(ANALYTICS_FILTERS_QUERY_KEYS.comparisonEndDate);
    return;
  }

  params.set(
    ANALYTICS_FILTERS_QUERY_KEYS.comparisonStartDate,
    persisted.comparisonStartDate,
  );
  params.set(
    ANALYTICS_FILTERS_QUERY_KEYS.comparisonEndDate,
    persisted.comparisonEndDate,
  );
}
